/**
 * FASE 22.6 — WORKFLOW INTENT RESOLVER
 *
 * Detects workflow-related intent from user messages.
 * Purely deterministic — regex patterns, no LLM.
 */

import {
  WORKFLOW_TYPES,
  WORKFLOW_STATUS,
  getWorkflowDefinition,
} from "./workflowSchemas";
import { getActiveWorkflows } from "./workflowState";

/**
 * @typedef {Object} WorkflowIntentResult
 * @property {"start"|"advance"|"complete"|"pause"|"query"|"none"} action
 * @property {string|null} type         — WORKFLOW_TYPES value
 * @property {string|null} workflowId   — matched existing workflow id
 * @property {number}      confidence   — 0-1
 * @property {string|null} entityHint   — name/id extracted from text
 */

/* ─── Intent pattern registry ─────────────────────────────────────── */

const START_PATTERNS = [
  /iniciar?\s+(seguimiento|proceso|workflow|flujo)/i,
  /comenzar?\s+(seguimiento|proceso|workflow|flujo)/i,
  /abrir?\s+(seguimiento|proceso|workflow|flujo)/i,
  /crear?\s+(seguimiento|proceso|workflow|flujo)/i,
  /empezar?\s+(seguimiento|proceso|workflow|flujo)/i,
  /nuevo\s+(seguimiento|proceso|bautismo|visita)/i,
];

const ADVANCE_PATTERNS = [
  /avanzar?\s+(paso|etapa|proceso)/i,
  /marcar?\s+(como\s+)?(hecho|completado|listo)/i,
  /completar?\s+(paso|etapa)/i,
  /siguiente\s+(paso|etapa)/i,
  /paso\s+completado/i,
  /ya\s+(visit[eé]|llam[eé]|habl[eé])/i,
];

const COMPLETE_PATTERNS = [
  /cerrar?\s+(seguimiento|proceso|workflow|flujo)/i,
  /completar?\s+(seguimiento|proceso|workflow|flujo)/i,
  /finalizar?\s+(seguimiento|proceso|workflow|flujo)/i,
  /terminar?\s+(seguimiento|proceso|workflow|flujo)/i,
  /ya\s+se\s+(bautiz[oó]|resolvi[oó]|integr[oó])/i,
];

const PAUSE_PATTERNS = [
  /pausar?\s+(seguimiento|proceso|workflow|flujo)/i,
  /suspender?\s+(seguimiento|proceso|workflow|flujo)/i,
  /detener?\s+(seguimiento|proceso|workflow|flujo)/i,
];

const QUERY_PATTERNS = [
  /cu[aá]ntos?\s+workflows?/i,
  /qu[eé]\s+(seguimientos?|procesos?)\s+(tengo|hay|est[aá]n)/i,
  /ver?\s+(seguimientos?|procesos?)/i,
  /listar?\s+(seguimientos?|procesos?)/i,
  /mostrar?\s+(seguimientos?|procesos?)/i,
  /estado\s+de\s+(los\s+)?(seguimientos?|procesos?)/i,
];

/* ─── Workflow type keyword map ─────────────────────────────────── */

const TYPE_KEYWORDS = {
  [WORKFLOW_TYPES.FAMILY_FOLLOWUP]:          [/seguimiento\s+familiar/i, /familia\s+.*seguimiento/i, /seguimiento.*familia/i],
  [WORKFLOW_TYPES.BAPTISM_PREPARATION]:      [/bautismo/i, /preparaci[oó]n.*bautismo/i, /candidato.*bautismo/i],
  [WORKFLOW_TYPES.PRAYER_FOLLOWUP]:          [/seguimiento.*oraci[oó]n/i, /oraci[oó]n.*seguimiento/i, /petici[oó]n.*oraci[oó]n/i],
  [WORKFLOW_TYPES.INACTIVE_MEMBER_RECOVERY]: [/miembro\s+inactivo/i, /recuperaci[oó]n/i, /distanciado/i, /restauraci[oó]n/i],
  [WORKFLOW_TYPES.VISIT_CYCLE]:              [/ciclo.*visita/i, /visitas?\s+pastorales?/i, /ronda\s+de\s+visitas?/i],
  [WORKFLOW_TYPES.EVENT_PREPARATION]:        [/preparaci[oó]n.*evento/i, /evento\s+pastoral/i, /organizar.*evento/i],
};

function detectWorkflowType(text) {
  for (const [type, patterns] of Object.entries(TYPE_KEYWORDS)) {
    if (patterns.some((p) => p.test(text))) return type;
  }
  return null;
}

function matchExistingWorkflow(text, type) {
  const wfs = getActiveWorkflows();
  if (!wfs.length) return null;

  // Try to match by entity name mention in text
  for (const wf of wfs) {
    if (type && wf.type !== type) continue;
    for (const entity of wf.related_entities) {
      if (entity.name && text.toLowerCase().includes(entity.name.toLowerCase())) {
        return wf.id;
      }
    }
  }

  // If type matched and only one workflow of that type, use it
  if (type) {
    const typed = wfs.filter((w) => w.type === type);
    if (typed.length === 1) return typed[0].id;
  }

  return null;
}

/**
 * Resolve workflow-related intent from a user message.
 * @param {string} text
 * @param {object|null} entityContext — current entity (for entity name matching)
 * @returns {WorkflowIntentResult}
 */
export function resolveWorkflowIntent(text, entityContext = null) {
  if (!text?.trim()) {
    return { action: "none", type: null, workflowId: null, confidence: 0, entityHint: null };
  }

  const lc = text.trim();

  let action     = "none";
  let confidence = 0;

  if (COMPLETE_PATTERNS.some((p) => p.test(lc))) {
    action     = "complete";
    confidence = 0.80;
  } else if (ADVANCE_PATTERNS.some((p) => p.test(lc))) {
    action     = "advance";
    confidence = 0.75;
  } else if (PAUSE_PATTERNS.some((p) => p.test(lc))) {
    action     = "pause";
    confidence = 0.80;
  } else if (START_PATTERNS.some((p) => p.test(lc))) {
    action     = "start";
    confidence = 0.75;
  } else if (QUERY_PATTERNS.some((p) => p.test(lc))) {
    action     = "query";
    confidence = 0.85;
  }

  if (action === "none") {
    return { action: "none", type: null, workflowId: null, confidence: 0, entityHint: null };
  }

  const type       = detectWorkflowType(lc);
  const workflowId = matchExistingWorkflow(lc, type);
  const entityHint = entityContext?.entityName ?? null;

  // Boost confidence if type or entity matched
  if (type)       confidence = Math.min(0.95, confidence + 0.10);
  if (workflowId) confidence = Math.min(0.95, confidence + 0.05);

  return { action, type, workflowId, confidence, entityHint };
}
